import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/session';

const AuthPage = lazy(() => import('./pages/AuthPage').then((module) => ({ default: module.AuthPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then((module) => ({ default: module.OnboardingPage })));
const AppShell = lazy(() => import('./shell/AppShell').then((module) => ({ default: module.AppShell })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then((module) => ({ default: module.LibraryPage })));
const DataSourcesPage = lazy(() => import('./pages/DataSourcesPage').then((module) => ({ default: module.DataSourcesPage })));
const BrandPage = lazy(() => import('./pages/BrandPage').then((module) => ({ default: module.BrandPage })));
const ModelsPage = lazy(() => import('./pages/ModelsPage').then((module) => ({ default: module.ModelsPage })));
const UsagePage = lazy(() => import('./pages/UsagePage').then((module) => ({ default: module.UsagePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const EditorPage = lazy(() => import('./pages/EditorPage').then((module) => ({ default: module.EditorPage })));

export function App() {
  return (
    <SessionProvider>
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/auth/sign-in" element={<AuthPage mode="sign-in" />} />
          <Route path="/auth/sign-up" element={<AuthPage mode="sign-up" />} />
          <Route path="/auth/signed-out" element={<AuthPage mode="signed-out" />} />
          <Route path="/auth/forgot-password" element={<AuthPage mode="forgot-password" />} />
          <Route path="/auth/check-email" element={<AuthPage mode="check-email" />} />
          <Route path="/auth/reset-password" element={<AuthPage mode="reset-password" />} />
          <Route path="/auth/password-reset" element={<AuthPage mode="password-reset" />} />
          <Route path="/onboarding" element={<RequireSession><OnboardingPage /></RequireSession>} />
          <Route path="/design/:documentId" element={<RequireSession><EditorPage /></RequireSession>} />
          <Route path="/app" element={<AppShell />}>
            <Route index element={<Navigate to="/app/library" replace />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="data-sources" element={<DataSourcesPage />} />
            <Route path="brand" element={<BrandPage />} />
            <Route path="models" element={<ModelsPage />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/app/library" replace />} />
        </Routes>
      </Suspense>
    </SessionProvider>
  );
}

function RequireSession({ children }: { children: ReactNode }) {
  const { signedIn } = useSession();
  const location = useLocation();
  if (!signedIn) return <Navigate to="/auth/sign-in" state={{ from: `${location.pathname}${location.search}` }} replace />;
  return children;
}

function RouteLoading() {
  return (
    <main className="route-loading" aria-label="Loading mGenie">
      <aside><span /><i /><i /><i /><i /></aside>
      <section><header /><div><span /><span /><span /></div><article /></section>
      <aside><span /><i /><i /><i /></aside>
    </main>
  );
}
